import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import { forceChangeMemberPassword } from '../../controllers/memberManagementController';
import { DIALOG_INPUT_SX, LabeledControl } from './EditMemberDialog';

export const MEMBER_ACCOUNT_TYPE = 0;
export const SUB_MEMBER_ACCOUNT_TYPE = 1;

const ForceChangePasswordDialog = ({ open, onClose, accountId, memberName, accountType = MEMBER_ACCOUNT_TYPE }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ next: false, conf: false });

  useEffect(() => {
    if (!open) return;
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setLoading(false);
    setShow({ next: false, conf: false });
  }, [open, accountId, accountType]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const adornment = (key) => (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))}
        edge="end"
        aria-label="şifre görünürlüğü"
        size="small"
      >
        {show[key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountId) return;

    if (newPassword !== confirmPassword) {
      setError('Yeni şifre ile doğrulama uyuşmuyor.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await forceChangeMemberPassword({
        accountId,
        accountType,
        newPassword,
        confirmPassword,
      });
      toast.success('Şifre başarıyla değiştirildi.');
      handleClose();
    } catch (err) {
      setError(err?.message || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        handleClose();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Şifre Değiştir{memberName ? ` — ${memberName}` : ''}
        <IconButton
          type="button"
          onClick={handleClose}
          size="small"
          disabled={loading}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <LabeledControl label="Yeni Şifre">
              <TextField
                type={show.next ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                disabled={loading}
                autoComplete="new-password"
                placeholder=""
                sx={DIALOG_INPUT_SX}
                InputProps={{ endAdornment: adornment('next') }}
              />
            </LabeledControl>
            <LabeledControl label="Yeni Şifre (Tekrar)">
              <TextField
                type={show.conf ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                disabled={loading}
                autoComplete="new-password"
                placeholder=""
                sx={DIALOG_INPUT_SX}
                InputProps={{ endAdornment: adornment('conf') }}
              />
            </LabeledControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            type="button"
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !newPassword || !confirmPassword}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0a3d7a',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ForceChangePasswordDialog;
